import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AlertHistory from '../components/security/AlertHistory';
import { GuestDetail, GuestHistory, StayRecord } from '../types/security';
import { securityService } from '../services/securityService';

// Composant modal pour afficher la photo en plein écran
const PhotoModal: React.FC<{ photoUrl: string; onClose: () => void; title: string }> = ({ photoUrl, onClose, title }) => (
  <div 
    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    onClick={onClose}
  >
    <div className="relative max-w-4xl max-h-[90vh] p-4">
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <h3 className="text-white text-center mb-4 font-semibold">{title}</h3>
      <img
        src={photoUrl}
        alt={title}
        className="max-w-full max-h-[80vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  </div>
);

const GuestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [guest, setGuest] = useState<GuestDetail | null>(null);
  const [history, setHistory] = useState<GuestHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [photoModalUrl, setPhotoModalUrl] = useState<string | null>(null);
  const [photoModalTitle, setPhotoModalTitle] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'details' | 'documents' | 'anomalies' | 'history'>('details');

  const loadGuestData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      const [guestData, historyData] = await Promise.all([
        securityService.getGuestById(id),
        securityService.getGuestHistory(id),
      ]);

      setGuest(guestData);
      setHistory(historyData);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError('Invité non trouvé');
      } else {
        setError('Erreur lors du chargement des données. Veuillez réessayer.');
      }
      console.error('Erreur chargement données invité:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadGuestData();
  }, [loadGuestData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-500">
        <svg className="w-16 h-16 mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-lg mb-4">{error}</p>
        <div className="flex space-x-3">
          <button
            onClick={loadGuestData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Recharger
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  if (!guest) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-500">
        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p>Invité non trouvé</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Retour
        </button>
      </div>
    );
  }

  const stays: StayRecord[] = history?.stays ?? [];
  const alerts = history?.alerts ?? [];

  const openPhotoModal = (url: string, title: string) => {
    setPhotoModalUrl(url);
    setPhotoModalTitle(title);
  };

  const closePhotoModal = () => {
    setPhotoModalUrl(null);
    setPhotoModalTitle('');
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Modal photo plein écran */}
      {photoModalUrl && (
        <PhotoModal 
          photoUrl={photoModalUrl} 
          onClose={closePhotoModal} 
          title={photoModalTitle}
        />
      )}
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            {guest.photoUrl ? (
              <img
                src={guest.photoUrl}
                alt={`${guest.firstName} ${guest.lastName}`}
                className="w-24 h-24 rounded-lg object-cover"
              />
            ) : (
              <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )}

            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {guest.firstName} {guest.lastName}
              </h1>
              <p className="text-gray-600">Document : {guest.documentNumber}</p>
              <p className="text-gray-600">Nationalité : {guest.nationality}</p>
              <p className="text-gray-600">Date de naissance : {guest.dateOfBirth}</p>
              {guest.phoneNumber && (
                <p className="text-gray-600">Téléphone : {guest.phoneNumber}</p>
              )}
              {guest.email && (
                <p className="text-gray-600">Email : {guest.email}</p>
              )}
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={loadGuestData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Actualiser
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Retour
            </button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne gauche — Infos invité */}
        <div className="lg:col-span-1 space-y-6">
          {/* Informations de séjour */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Informations de séjour</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Hôtel</p>
                <p className="font-medium">{guest.hotelName}</p>
              </div>
              {((guest as any).hotelAddress || (guest as any).hotelCity) && (
                <div>
                  <p className="text-sm text-gray-600">Adresse</p>
                  <p className="font-medium">{(guest as any).hotelAddress}</p>
                  <p className="text-sm text-gray-500">{[(guest as any).hotelCity, (guest as any).hotelProvince].filter(Boolean).join(', ')}</p>
                </div>
              )}
              {(guest as any).hotelLatitude && (guest as any).hotelLongitude && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Localisation</p>
                  <a
                    href={`https://www.google.com/maps?q=${(guest as any).hotelLatitude},${(guest as any).hotelLongitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Voir sur la carte
                  </a>
                  <iframe
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${(guest as any).hotelLongitude - 0.005},${(guest as any).hotelLatitude - 0.003},${(guest as any).hotelLongitude + 0.005},${(guest as any).hotelLatitude + 0.003}&layer=mapnik&marker=${(guest as any).hotelLatitude},${(guest as any).hotelLongitude}`}
                    className="w-full h-40 rounded-lg border border-gray-200 mt-2"
                    style={{ border: 0 }}
                  />
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Numéro de chambre</p>
                <p className="font-medium">{guest.roomNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date d'arrivée</p>
                <p className="font-medium">{guest.checkinDate}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date de départ</p>
                <p className="font-medium">{guest.checkoutDate}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Statut</p>
                <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${
                  guest.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                  guest.status === 'checked_out' ? 'bg-gray-100 text-gray-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {guest.status === 'checked_in' ? 'Enregistré' :
                   guest.status === 'checked_out' ? 'Parti' :
                   guest.status}
                </span>
              </div>
            </div>
          </div>

          {/* Scan du document d'identité */}
          {guest.documentScanUrl && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Document d'identité</h3>
              <img
                src={guest.documentScanUrl}
                alt="Scan du document d'identité"
                className="w-full rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openPhotoModal(guest.documentScanUrl!, 'Document d\'identité')}
              />
            </div>
          )}

          {/* Photo d'identité */}
          {guest.identityPhotoUrl && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Photo d'identité</h3>
              <img
                src={guest.identityPhotoUrl}
                alt="Photo d'identité"
                className="w-full rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openPhotoModal(guest.identityPhotoUrl!, 'Photo d\'identité')}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">Cliquez pour agrandir</p>
            </div>
          )}
        </div>

        {/* Colonne droite — Historique et alertes */}
        <div className="lg:col-span-2 space-y-6">
          {/* Historique des séjours */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Historique des séjours ({stays.length})
            </h3>
            {stays.length === 0 ? (
              <p className="text-gray-500">Aucun séjour enregistré</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hôtel</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chambre</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arrivée</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Départ</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motif</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stays.map((stay) => (
                      <tr key={stay.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">{stay.hotelName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{stay.roomNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{stay.checkinDate}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{stay.checkoutDate}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{stay.stayPurpose ?? '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            stay.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                            stay.status === 'checked_out' ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {stay.status === 'checked_in' ? 'Enregistré' :
                             stay.status === 'checked_out' ? 'Parti' :
                             stay.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Alertes de sécurité */}
          {alerts.length > 0 && (
            <AlertHistory alerts={alerts} onAlertAcknowledged={loadGuestData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestDetailPage;
